import { useEffect, useState } from "react";
import { getStockSnapshot } from "../marketData/marketDataApi";
import "./LiveMarketOverview.css";

const marketIntelligence = [
  {
    name: "S&P 500",
    symbol: "SPX",
    value: "6,205.12",
    change: "+0.42%",
    points: "+25.84",
  },
  {
    name: "Dow Jones",
    symbol: "DJIA",
    value: "45,120.31",
    change: "+0.18%",
    points: "+81.36",
  },
  {
    name: "NASDAQ",
    symbol: "IXIC",
    value: "21,398.44",
    change: "+0.61%",
    points: "+129.47",
  },
  {
    name: "NASDAQ 100",
    symbol: "NDX",
    value: "22,806.15",
    change: "+0.58%",
    points: "+130.91",
  },
  {
    name: "Russell 2000",
    symbol: "RUT",
    value: "2,236.45",
    change: "+0.27%",
    points: "+6.07",
  },
  {
    name: "Volatility",
    symbol: "VIX",
    value: "17.22",
    change: "-0.21%",
    points: "-0.04",
  },
  {
    name: "10-Year Treasury",
    symbol: "US10Y",
    value: "4.28%",
    change: "+0.03",
    points: "Yield",
  },
  {
    name: "U.S. Dollar",
    symbol: "DXY",
    value: "98.74",
    change: "+0.12%",
    points: "+0.12",
  },
];

function initialSymbol() {
  try {
    return localStorage.getItem("aninaSelectedSymbol") || "PLUG";
  } catch (error) {
    return "PLUG";
  }
}

function formatPrice(value) {
  return typeof value === "number" && Number.isFinite(value)
    ? `$${value.toFixed(2)}`
    : "—";
}

function formatQuote(snapshot) {
  const bid = snapshot?.latest_quote?.bp;
  const ask = snapshot?.latest_quote?.ap;
  if (typeof bid !== "number" || typeof ask !== "number") return "Bid / ask unavailable";
  return `Bid ${formatPrice(bid)} • Ask ${formatPrice(ask)}`;
}

function formatTimestamp(value) {
  if (!value) return "Update time unavailable";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Update time unavailable";
  return `Updated ${parsed.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  })}`;
}

function coverageText(source) {
  const coverage = source?.integration_coverage;
  if (!coverage) return "Stocks: snapshot V1";
  return `Stocks: snapshot V1 • Options: ${coverage.options === "not_connected" ? "not connected" : coverage.options} • Level II: ${coverage.level_ii === "unavailable" ? "unavailable" : coverage.level_ii}`;
}

function LiveMarketOverview() {
  const [selectedSymbol, setSelectedSymbol] = useState(initialSymbol);
  const [snapshotState, setSnapshotState] = useState({
    status: "loading",
    data: null,
    error: "",
  });

  useEffect(() => {
    const handleSymbolChange = (event) => {
      const nextSymbol = event.detail?.symbol;
      if (nextSymbol) setSelectedSymbol(String(nextSymbol).trim().toUpperCase());
    };

    window.addEventListener("nina:symbol-change", handleSymbolChange);
    return () => window.removeEventListener("nina:symbol-change", handleSymbolChange);
  }, []);

  useEffect(() => {
    let active = true;
    setSnapshotState({ status: "loading", data: null, error: "" });

    getStockSnapshot(selectedSymbol)
      .then((data) => {
        if (!active) return;
        setSnapshotState({ status: "ready", data, error: "" });
      })
      .catch((error) => {
        if (!active) return;
        setSnapshotState({
          status: "error",
          data: null,
          error: error.message || "Market data is unavailable.",
        });
      });

    return () => {
      active = false;
    };
  }, [selectedSymbol]);

  const snapshot = snapshotState.data;
  const selectedPrice = snapshot?.latest_trade?.p ?? snapshot?.minute_bar?.c;
  const source = snapshot?.source;

  return (
    <section
      className="liveMarketOverview"
      aria-label="Market intelligence overview with Alpaca selected-symbol data and demo macro data"
    >
      <div className="liveMarketOverview__label">
        <span>MARKET</span>
        <strong>INTELLIGENCE</strong>
      </div>

      <div className="liveMarketOverview__track">
        <article className="liveMarketOverview__item liveMarketOverview__providerItem">
          <div className="liveMarketOverview__name">
            <strong>{selectedSymbol}</strong>
            <span>Selected stock</span>
          </div>

          <div className="liveMarketOverview__quote liveMarketOverview__providerQuote">
            {snapshotState.status === "loading" && (
              <>
                <span>Connecting</span>
                <strong>ALPACA</strong>
                <small>Server-configured feed requested</small>
              </>
            )}

            {snapshotState.status === "error" && (
              <>
                <span>Unavailable</span>
                <strong className="negative">NO ALPACA DATA</strong>
                <small title={snapshotState.error}>{snapshotState.error}</small>
              </>
            )}

            {snapshotState.status === "ready" && (
              <>
                <span>{formatPrice(selectedPrice)}</span>
                <strong>{source?.label || "Alpaca Market Data"}</strong>
                <small>{formatQuote(snapshot)}</small>
              </>
            )}
          </div>

          {snapshotState.status === "ready" && source && (
            <div className="liveMarketOverview__sourceDetails" title={source.coverage}>
              <span>{source.provider}</span>
              <strong>{source.feed?.toUpperCase()}</strong>
              <small>{source.quality_level || source.quality_state}</small>
              <small>{formatTimestamp(source.last_update_at)}</small>
              <small>{coverageText(source)}</small>
            </div>
          )}

        </article>

        {marketIntelligence.map((market) => {
          const isNegative = market.change.startsWith("-");

          return (
            <article className="liveMarketOverview__item" key={market.symbol}>
              <div className="liveMarketOverview__name">
                <strong>{market.name}</strong>
                <span>{market.symbol}</span>
              </div>

              <div className="liveMarketOverview__quote">
                <span>{market.value}</span>
                <strong className={isNegative ? "negative" : ""}>
                  {isNegative ? "▼" : "▲"} {market.change}
                </strong>
                <small>{market.points}</small>
              </div>
            </article>
          );
        })}
      </div>

      <span
        className="liveMarketOverview__dataStatus liveMarketOverview__mixedStatus"
        title="Selected-stock data comes from Alpaca when configured. Macro/index tiles remain demo data."
      >
        MIXED
      </span>
    </section>
  );
}

export default LiveMarketOverview;
